import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import ImageUpload from "./ImageUpload";

const EMPTY = { country: "", flag: "🌍", image: "", processing_time: "", fees_from: "", description: "", eligibility: "", documents: "" };

export default function VisaFormModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial ? {
    ...EMPTY, ...initial,
    fees_from: String(initial.fees_from || ""),
    eligibility: (initial.eligibility || []).join("\n"),
    documents: (initial.documents || []).join("\n"),
  } : EMPTY);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.country || !f.image) { toast.error("Country and image are required"); return; }
    setLoading(true);
    const payload = {
      country: f.country, flag: f.flag || "🌍", image: f.image,
      processing_time: f.processing_time, fees_from: Number(f.fees_from) || 0,
      description: f.description,
      eligibility: f.eligibility.split("\n").map((s) => s.trim()).filter(Boolean),
      documents: f.documents.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (initial) await api.patch(`/admin/visa/${initial.id}`, payload);
      else await api.post("/admin/visa", payload);
      toast.success(initial ? "Visa updated" : "Visa created");
      onSaved(); onClose();
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  const input = "w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[#006CE4] focus:ring-4 focus:ring-blue-100 transition";
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="visa-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h3 className="h3">{initial ? "Edit Visa" : "Add New Visa"}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full" data-testid="visa-form-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className="text-xs font-semibold muted">Country *</label><input required name="country" value={f.country} onChange={onChange} className={input} placeholder="e.g. Singapore" data-testid="visa-form-country" /></div>
          <div><label className="text-xs font-semibold muted">Flag emoji</label><input name="flag" value={f.flag} onChange={onChange} className={input} placeholder="🇸🇬" data-testid="visa-form-flag" /></div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold muted">Hero Image *</label>
            <ImageUpload value={f.image} onChange={(url) => setF({ ...f, image: url })} testId="visa-form-image" />
          </div>
          <div><label className="text-xs font-semibold muted">Processing Time</label><input name="processing_time" value={f.processing_time} onChange={onChange} className={input} placeholder="e.g. 3-5 working days" data-testid="visa-form-time" /></div>
          <div><label className="text-xs font-semibold muted">Fees From (₹)</label><input type="number" name="fees_from" value={f.fees_from} onChange={onChange} className={input} placeholder="e.g. 2500" data-testid="visa-form-fees" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Description</label><textarea name="description" value={f.description} onChange={onChange} rows="2" className={`${input} resize-none`} data-testid="visa-form-description" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Eligibility (one per line)</label><textarea name="eligibility" value={f.eligibility} onChange={onChange} rows="4" className={`${input} resize-none`} placeholder={"Valid passport (6 months)\nConfirmed return tickets"} data-testid="visa-form-eligibility" /></div>
          <div className="sm:col-span-2"><label className="text-xs font-semibold muted">Documents (one per line)</label><textarea name="documents" value={f.documents} onChange={onChange} rows="4" className={`${input} resize-none`} placeholder={"Passport scan\nPhoto (35x45mm)\nBank statements"} data-testid="visa-form-documents" /></div>
          <div className="sm:col-span-2 flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline flex-1 justify-center" data-testid="visa-form-cancel">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 justify-center disabled:opacity-60" data-testid="visa-form-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : initial ? "Save Changes" : "Create Visa"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
